<template>
  <div class="agency-event-ranking-entry">
    <div class="ranking-cell">
      <div class="main-component">
        <div class="rank-wrap">
          <div class="rank">
            <p v-if="isRanker" :style="rankStyle" class="rank__ranker">
              {{ rankingItem.rank }}
            </p>
            <p v-else class="rank__normal">
              {{ rankingItem.rank }}<span>{{ suffix }}</span>
            </p>
          </div>
        </div>

        <agency-event-ranking-streamer-icon
          :streamer="streamerUser"
          :currentStatus="rankingItem.currentStatus"
          @firebaseEvent="logEvent"
        />

        <div
          class="main-information"
          role="button"
          tabindex="0"
          @click="openProfile"
          @keydown.enter="openProfile"
          @keydown.space.prevent="openProfile"
        >
          <div class="main-information__content">
            <p class="main-information__name">{{ rankingItem.username }}</p>
            <p class="main-information__score">
              <span>{{ formattedScore }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import type { AgencyEventStreamerUser } from '@/models/StreamerUser'
import formatter from '@/utils/Formatter'
import { getRankOrdinalSuffix } from '@/i18n'
import { Client, defaultNativeInterfaceClient } from '@/utils/NativeInterface'
import AgencyEventRankingStreamerIcon from './AgencyEventRankingStreamerIcon.vue'
import type { AgencyEventRankingIndividual } from '@/api/pb/AgencyEventRanking/Common/AgencyEventRankingIndividual'
import { defaultFirebaseAnalytics } from '@/utils/firebase'
import { logEvent } from 'firebase/analytics'

const badgeImages: { [k: number]: string } = {
  1: '/img/badgeBgFirst.png',
  2: '/img/badgeBgSecond.png',
  3: '/img/badgeBgThird.png',
}

@Component({
  components: {
    AgencyEventRankingStreamerIcon,
  },
})
export default class AgencyEventRankingEntry extends Vue {
  @Prop({ required: true })
  rankingItem!: AgencyEventRankingIndividual

  @Prop({ required: true })
  index!: number

  get isRanker() {
    return this.rankingItem.rank <= 3
  }

  get rankStyle() {
    const bgImage = badgeImages[this.rankingItem.rank]
    if (bgImage) {
      return {
        'background-image': `url(${bgImage})`,
      }
    }
    return null
  }

  get suffix() {
    return getRankOrdinalSuffix(this.rankingItem.rank)
  }

  get formattedScore() {
    return formatter.numberWithCommas(this.rankingItem.score)
  }

  get streamerUser(): AgencyEventStreamerUser {
    return {
      profileId: this.rankingItem.profileId,
      iconUrl: this.rankingItem.iconUrl,
    }
  }

  openProfile() {
    this.logEvent(null)

    const nativeInterface = defaultNativeInterfaceClient()
    if (nativeInterface.client !== Client.native) {
      try {
        nativeInterface.openProfile(this.rankingItem.profileId)
        return
      } catch {
        // catch して web フォールバックに進める
      }
    }

    // webのプロフィールページに遷移する
    location.href = `/profile/${this.rankingItem.profileId}`
  }

  logEvent(stateType: string | null) {
    const params = {
      selected_vlive_id: this.rankingItem.vliveId,
      firebase_screen: 'WebView',
      state_type: stateType,
      url: location.href,
    }
    logEvent(defaultFirebaseAnalytics, 'select_state_type', params)
  }
}
</script>

<style scoped>
.agency-event-ranking-entry {
  border-bottom: 1px solid #eeeeee;
  color: #262626;
}

.ranking-cell {
  background-color: #ffffff;
}

.main-component {
  display: flex;
  height: 76px;
  max-width: 500px;
  margin: 0 auto;
}

.rank-wrap {
  display: inline-block;
  width: 58px;
  min-width: 58px;
  height: 24px;
  margin: 26px 4px 26px 16px;
}

.rank {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 24px;
}

.rank__ranker {
  background-size: contain;
  color: #ffffff;
  width: 24px;
  height: 24px;
  line-height: 24px;
  font-size: 14px;
  font-weight: 700;
  margin: 0;
}

.rank__normal {
  vertical-align: middle;
  line-height: 16px;
  font-weight: 600;
  margin: 0;
}

.rank__normal span {
  font-size: 12px;
}

.main-information {
  display: inline-block;
  margin: 0 22px 0 12px;
  min-width: 217px;
}

.main-information__content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  height: 76px;
}

.main-information__content p {
  flex-basis: 100%;
  margin: 0;
}

.main-information__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-information__score {
  font-weight: 600;
  font-size: 12px;
  vertical-align: middle;
  line-height: 150%;
}

.main-information__score span {
  color: #188dff;
}
</style>
